import { Select } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'
import { IssueRiskAspectSelect } from '@/components_deprecated/Select/IssueRiskAspectSelect'
import type { UserNode } from '@/modules/users'
import type { SelectOption } from '@ur-solutions/react-components'
import type { FormErrors, FormOnChangeFn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { set } from 'date-fns'
import React, { useState } from 'react'
import styled from 'styled-components'
import type { ShallowCategory } from '.'
import { CategorySelect } from '.'
import { useIssueSeverityOptions } from '../consts'
import type { EditIssueForm } from '../types'
import { DatePicker, TimePicker } from '@/components'

const Wrapper = styled.section`
  grid-area: main;

  display: grid;
  grid-template-columns: repeat(3, auto);

  span.unknown {
    color: ${props => props.theme.colors.red};
  }

  ${props => props.theme.media.mobile} {
    display: flex;
    flex-direction: column;

    & > div {
      border: 1px solid ${props => props.theme.colors.gray8} !important;
      border-width: 1px 0 0 0 !important;

      &.category {
        order: -1;
        border-width: 0 !important;
      }

      &.date {
        order: -1;
      }
    }
  }
`
const Info = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  gap: 0.5rem;

  & + div {
    border-left: 1px solid ${props => props.theme.colors.gray8};

    &:nth-child(4) {
      border-left: 0;
    }

    &:nth-child(n + 4) {
      border-top: 1px solid ${props => props.theme.colors.gray8};
    }

    &:nth-child(5) {
      grid-column: 2 / -1;
    }
  }

  & > span {
    font-weight: 600;
    align-items: center;
    display: flex;
  }

  & > span {
    color: ${props => props.theme.colors.gray5};
  }

  p {
    margin: 0;
    font-size: 1.6em;
  }

  ${props => props.theme.media.mobile} {
    padding: 1rem;

    span {
      font-size: 1rem;
    }

    p {
      font-size: 1rem;
    }
  }
`
const Editor = styled.div`
  margin-top: 0.5rem;
`
const DateEditor = styled(Editor)`
  display: flex;

  & > * + * {
    margin-left: 0.5rem;
  }
`
const TimeLabel = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${props => props.theme.colors.gray5};
`

const TimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

interface CreateMainInfoProps {
  form: EditIssueForm
  errors: FormErrors<EditIssueForm>
  updateForm: (values: Partial<EditIssueForm>) => void
  onChange: FormOnChangeFn<EditIssueForm>
  subcategoryOptions: SelectOption<string, any>[]
  subcategoriesLoading: boolean
}

export const CreateMainInfo: React.FC<CreateMainInfoProps> = ({
  form,
  errors,
  updateForm,
  onChange,
  subcategoryOptions,
  subcategoriesLoading,
}) => {
  const translations = useTranslate({
    category: 'common.category',
    responsible: 'common.responsible',
    date: 'common.date',
    severity: 'common.severity',
    riskAspect: 'common.risk-aspect',
    subCategory: 'common.sub-category',
    select: 'common.select',
    none: 'common.none',
    dataFrom: 'common.data-from',
    dataTo: 'common.data-to',
  })
  const severityOptions = useIssueSeverityOptions()

  const [temporaryResponsible, setTemporaryResponsible] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)

  function handleChangeCategory(id: string, category: ShallowCategory | null) {
    updateForm({
      category: id,
      subcategory: null,
    })

    setTemporaryResponsible(category?.responsibleUser ?? null)
  }

  function handleChangeDate(date: Date, fieldName: keyof EditIssueForm) {
    const baseDate =
      form[fieldName] instanceof Date ? form[fieldName] : new Date()

    const newDate = set(date, {
      hours: baseDate.getHours(),
      minutes: baseDate.getMinutes(),
      seconds: baseDate.getSeconds(),
    })

    if (fieldName === 'date' && form.dateEnd && newDate > form.dateEnd) {
      onChange('dateEnd')(newDate)
    }

    onChange(fieldName)(newDate)
  }

  // TODO these should not exist
  // useEffect(() => {
  //   if (form?.date) {
  //     const newDateEnd = new Date(form.date.getTime())
  //     onChange('dateEnd')(newDateEnd)
  //   }
  // }, [form?.date])

  // useEffect(() => {
  //   onChange('subcategory')(null)
  // }, [form.category])

  return (
    <Wrapper>
      <Info className="category">
        <span>{translations.category}</span>
        <Editor>
          <CategorySelect
            value={form.category}
            error={errors.category}
            minWidth="260px"
            fullWidth
            placeholder={translations.select}
            onChange={handleChangeCategory}
          />
        </Editor>
        {form.category && subcategoryOptions.length > 0 && (
          <>
            <span>{translations.subCategory}</span>
            <Select
              loading={subcategoriesLoading}
              value={form.subcategory}
              options={subcategoryOptions}
              onChange={onChange('subcategory')}
              height="3rem"
              grayed
              fullWidth
              nullable
              error={errors.subcategory}
            />
          </>
        )}
      </Info>

      <Info>
        <span>{translations.responsible}</span>
        <p>
          {temporaryResponsible ? (
            <PlainLink
              to={`/users/${temporaryResponsible.id}`}
              $hoverColor="secondary"
            >
              {temporaryResponsible.fullName}
            </PlainLink>
          ) : (
            translations.none
          )}
        </p>
      </Info>

      <Info>
        <span>{translations.severity}</span>
        <Editor>
          <Select
            value={form.severity}
            options={severityOptions}
            onChange={onChange('severity')}
            height="3rem"
            grayed
            fullWidth
            nullable
          />
        </Editor>
      </Info>
      <Info>
        <span>{translations.riskAspect}</span>
        <Editor>
          <IssueRiskAspectSelect
            value={form.riskAspect}
            onChange={onChange('riskAspect')}
            height="3rem"
            fullWidth
            grayed
            nullable
          />
        </Editor>
      </Info>
      <Info className="date">
        <TimeContainer>
          <div>
            <TimeLabel>{translations.dataFrom}</TimeLabel>
            <DateEditor>
              <TimePicker value={form.date} onChange={onChange('date')} />
              <DatePicker
                mode="single"
                value={form.date}
                onChange={date =>
                  date ? handleChangeDate(date, 'date') : undefined
                }
              />
            </DateEditor>
          </div>
          <div>
            <TimeLabel>{translations.dataTo}</TimeLabel>
            <DateEditor>
              <TimePicker value={form.dateEnd} onChange={onChange('dateEnd')} />
              <DatePicker
                mode="single"
                value={form.dateEnd}
                onChange={date =>
                  date ? handleChangeDate(date, 'dateEnd') : undefined
                }
              />
            </DateEditor>
          </div>
        </TimeContainer>
      </Info>
    </Wrapper>
  )
}
